import { useApolloClient } from '@apollo/client';
import { Link, useRouter } from '@tanstack/react-router';
import { useAuthData, useUserRoles } from '@anytag/auth';
import { AiSidebarButton, DropdownMenu } from '@anytag/shared-atoms';
import { useGetHelpPageUrl, useToggle } from '@anytag/shared-hooks';
import { MenuTitle, removeToken } from '@anytag/router';
import { LANGUAGE_OPTIONS, TIKTOK_HELP_LINK } from '@anytag/shared-constants';
import { MenuTypeLevel } from '@anytag/shared-types';
import { useTranslation } from 'react-i18next';
import { HelpCircleContainedIcon, Bell02Icon } from '@anyui/icons';

export const HeaderMenu = () => {
  const client = useApolloClient();
  const { t, i18n } = useTranslation();
  const {
    auth: { name = '-', userId },
    dispatch
  } = useAuthData();
  const router = useRouter();
  const { on, toggle, setOff } = useToggle(false);
  const { isAdminStaffAgencyTalentAgencyPartner, isAdvertiser, isAdvertiserFromTikTok } = useUserRoles(); // shared with User Route permissions
  const helpCenterUrl = useGetHelpPageUrl();

  const handleSignOut = () => {
    removeToken();
    dispatch({ type: 'LOGOUT' });
    router.history.push('/sign-in');
    client.clearStore();
  };

  return (
    <div className="flex h-10 items-center justify-end gap-x-3">
      <a
        href={isAdvertiserFromTikTok ? TIKTOK_HELP_LINK.helpCenter : helpCenterUrl}
        target="_blank"
        className="inline-flex rounded-xs hover:ring-1 hover:ring-zinc-400"
        rel="noreferrer"
      >
        <HelpCircleContainedIcon width={16} height={16} color="gray" />
      </a>
      <Bell02Icon
        width={16}
        height={16}
        color="gray"
        className="cursor-pointer rounded-xs hover:ring-1 hover:ring-zinc-400"
      />
      <DropdownMenu
        open={on}
        onInteractOutside={setOff}
        trigger={
          <div
            onClick={toggle}
            data-letters={name?.charAt(0).toUpperCase()}
            className="size-5 cursor-pointer before:flex before:size-5 before:items-center before:justify-center before:rounded-full before:bg-black-90 before:text-[10px] before:font-bold before:text-white before:content-[attr(data-letters)]"
          />
        }
        options={[
          {
            value: 'user-settings',
            hidden: !(isAdminStaffAgencyTalentAgencyPartner || isAdvertiser),
            label: (
              <Link
                key="user-settings"
                title={MenuTitle.UserSettings}
                to={isAdvertiser ? '/user-settings' : '/user/$id'}
                params={{
                  id: userId?.toString()
                }}
                onClick={toggle}
              >
                {t(MenuTitle.UserSettings)}
              </Link>
            ),
            className: 'text-sm font-semibold text-primary h-8'
          },
          {
            label: t('languageTitle'),
            menuType: MenuTypeLevel.MULTI_LEVEL,
            onClick: (o) => {
              i18n.changeLanguage(o.value);
              toggle();
            },
            submenu: LANGUAGE_OPTIONS.map((option) => ({
              label: option.label,
              value: option.value
            })),
            className: 'min-w-30',
            value: 'language'
          },
          {
            label: MenuTitle.SignOut,
            value: 'sign-out',
            onClick: handleSignOut
          }
        ]}
      />
      <AiSidebarButton className="px-0!" />
    </div>
  );
};

// I don't know how this new panel should look like, so maybe we will need this later
// const roleEnumToShortName = (role: UserRoles | null) => {
//   switch (role) {
//     case UserRoles.ADMINISTRATOR:
//       return 'Admin';
//     case UserRoles.STAFF:
//       return 'Staff';
//     case UserRoles.ADVERTISER:
//       return 'Advertiser';
//     case UserRoles.AGENCY:
//       return 'Agency';
//     case UserRoles.TALENT_AGENCY:
//       return 'Talent Agency';
//     case UserRoles.PARTNER:
//       return 'Partner';

//     default:
//       return '';
//   }
// };
